import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MailIcon from '@mui/icons-material/MailOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { Alert, Backdrop, CircularProgress, ListItemIcon, Snackbar } from '@mui/material';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLazyGetUserListForExportQuery } from '../../../api/admin.api.ts';
import {
	useLazyExportAssetsQuery,
	useLazyExportNonLeasedAssetsQuery,
	useLazyGetAssetsByInvoiceNumberQuery,
} from '../../../api/asset.api.ts';
import { useLazyExportContractsQuery } from '../../../api/contract.api.ts';
import { useLazyExportInvoicesQuery, useLazyGetInvoiceQuery } from '../../../api/invoice.api.ts';
import type { FilterSearchRequestParams } from '../../../api/models/filters.model.ts';
import type { InvoiceAsset } from '../../../api/models/invoiceAsset.model.ts';
import type { InvoiceDetails } from '../../../api/models/invoiceDetails.model.ts';
import type { Role } from '../../../api/models/user.model.ts';
import { useGetCurrentUserQuery } from '../../../api/user.api.ts';
import {
	INVOICE_ASSET_EXPORT_CSV_HEADERS,
	INVOICE_ASSET_EXPORT_PAGE_SIZE,
	USERS_CSV_HEADERS,
} from '../../../constants/dataGrid.ts';
import { INVOICE_ASSETS_EXPORT } from '../../../constants/logic.ts';
import {
	useUserAccountRole,
	useUserAccountRoleHasRoleScope,
} from '../../../hooks/useUserAccountRole.ts';
import { selectAccount } from '../../../redux/slices/accountSlice.ts';
import { selectIsLoading } from '../../../redux/slices/loadingSlice.ts';
import {
	selectSelectedRecords,
	type BaseRecord,
} from '../../../redux/slices/selectedGridRecordsSlice.ts';
import type { RootState } from '../../../redux/store';
import { formatDateValue, generateCSV } from '../../../utils/helpers.ts';
import DeleteNonLeasedAssetsModal from '../../Modals/DeleteNonLeasedAssetsModal/DeleteNonLeasedAssetsModal.tsx';
import ModifyLeasedUserDefinedFieldsModal from '../../Modals/ModifyUserDefinedFieldsModal/ModifyLeasedUserDefinedFieldsModal.tsx';
import ModifyNonLeasedUserDefinedFieldsModal from '../../Modals/ModifyUserDefinedFieldsModal/ModifyNonLeasedUserDefinedFieldsModal.tsx';
import UploadLeasedAssetUDFModal from '../../Modals/UploadLeasedAssetUDFModal/UploadLeasedAssetUDFModal.tsx';
import UploadNonLeasedAssetsModal from '../../Modals/UploadNonLeasedAssetsModal/UploadNonLeasedAssetsModal';
import { DataGridMoreActions, type DataGridMoreAction } from '../DataGridMoreActions';
import type { DataGridType } from '../DataGridTypes.ts';

interface DataGridComponentProps {
	apiRef: React.RefObject<GridApiPro | null>;
	dataGridType: DataGridType;
	title: string;
	handleRequestClick: () => void;
	onRefresh?: () => void;
}

export interface DataGridActionContext {
	handleRequestClick: () => void;
	handleExportDataClick: () => Promise<void>;
	handlePrintClick: () => void;
	handleDownloadTemplateClick: () => void;
	role: Role | null;
	dataGridType: DataGridType;
	openUploadModal: () => void;
	openEditNonLeasedUDFModal: () => void;
	openEditLeasedUDFModal: () => void;
	selectedRows: BaseRecord[];
	openUploadLeasedAssetModal: () => void;
	openDeleteConfirmationModal: () => void;
}

// Flattens a single invoice-asset row into the shape expected by
// INVOICE_ASSET_EXPORT_CSV_HEADERS. Billing-address fields on the row are
// preferred; when the DTO leaves them as empty strings we fall back to the
// invoice header's billing address so the CSV still carries a value. `||` (not
// `??`) is deliberate here because the DTO types these as non-nullable strings
// and returns `""` when missing.
export const flattenInvoiceAssetRow = (
	asset: InvoiceAsset,
	invoiceHeader: InvoiceDetails | undefined,
	accountName: string
): Record<string, string | number | null | undefined> => {
	return {
		assetNumber: asset.assetNumber,
		assetQuantity: asset.assetQuantity,
		accountName: accountName,
		scheduleNo: asset.scheduleNo,
		invoiceNumber: asset.invoiceNumber ?? invoiceHeader?.invoiceNumber ?? '',
		invoiceDate:
			formatDateValue(asset.invoiceDate) ?? formatDateValue(invoiceHeader?.invoiceDate) ?? '',
		invoiceDueDate:
			formatDateValue(asset.invoiceDueDate) ?? formatDateValue(invoiceHeader?.invoiceDueDate) ?? '',
		purchaseOrderNo: asset.purchaseOrderNo,
		invoiceDescription: asset.invoiceDescription ?? invoiceHeader?.invoiceDescription ?? '',
		invoiceAmount: asset.invoiceAmount,
		taxAmount: asset.taxAmount,
		totalInvoiceAmount: asset.totalInvoiceAmount,
		assetCategory: asset.assetCategory,
		manufacturer: asset.manufacturer,
		modelNumber: asset.modelNumber,
		itemDescription: asset.itemDescription,
		eqLocationAddress1: asset.eqLocationAddress1,
		eqLocationAddress2: asset.eqLocationAddress2,
		eqLocationAddress3: asset.eqLocationAddress3,
		eqLocationCity: asset.eqLocationCity,
		eqLocationState: asset.eqLocationState,
		eqLocationZipCode: asset.eqLocationZipCode,
		eqLocationCounty: asset.eqLocationCounty,
		invoiceBillingAddress1: asset.invoiceBillingAddress1 || invoiceHeader?.billingAddress1 || '',
		invoiceBillingAddress2: asset.invoiceBillingAddress2 || invoiceHeader?.billingAddress2 || '',
		invoiceBillingAddress3: asset.invoiceBillingAddress3 || invoiceHeader?.billingAddress3 || '',
		invoiceBillingCity: asset.invoiceBillingCity || invoiceHeader?.billingCity || '',
		invoiceBillingState: asset.invoiceBillingState || invoiceHeader?.billingState || '',
		invoiceBillingPostalCode:
			asset.invoiceBillingPostalCode || invoiceHeader?.billingPostalCode || '',
		invoiceBillingCounty: asset.invoiceBillingCounty ?? '',
		invoicePastDue: invoiceHeader?.invoicePastDue ?? '',
		barcode: asset.barcode || '',
		glCode: asset.glCode || '',
		udf1: asset.udf1,
		udf2: asset.udf2,
		udf3: asset.udf3,
		udf4: asset.udf4,
	};
};

const DataGridToolbarActionsComponent: React.FC<DataGridComponentProps> = ({
	apiRef,
	dataGridType,
	title,
	handleRequestClick,
	onRefresh,
}) => {
	const [isError, setIsError] = useState(false);
	const globalIsLoading = useSelector(selectIsLoading);

	// Export hooks
	const [exportContracts] = useLazyExportContractsQuery();
	const [exportAssets] = useLazyExportAssetsQuery();
	const [exportInvoices] = useLazyExportInvoicesQuery();
	const [exportNLA] = useLazyExportNonLeasedAssetsQuery(); // Placeholder for Non-Leased Assets export hook
	const [exportUsers] = useLazyGetUserListForExportQuery();
	const [getInvoiceAssetsPage] = useLazyGetAssetsByInvoiceNumberQuery();
	const [getInvoiceHeader] = useLazyGetInvoiceQuery();

	// Get active global filters
	const activeFilters = useSelector((state: RootState) => state.filters.activeFilters);
	const selectedRows = useSelector((state: RootState) => selectSelectedRecords(state));
	// Optional chain guards test stores and non-invoice grids where the slice
	// may not be registered; the invoice-asset export explicitly requires this
	// value and throws below when absent.
	const selectedInvoiceNumber = useSelector(
		(state: RootState) => state.invoices?.selectedInvoiceDetailsViewId
	);

	// Export menu state
	const [anchorDesktop, setAnchorDesktop] = useState<null | HTMLElement>(null);
	const [anchorMobile, setAnchorMobile] = useState<null | HTMLElement>(null);

	// Modal state for upload
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [isUploadLeasedAssetModalOpen, setIsUploadLeasedAssetModalOpen] = useState(false);

	const [searchBoxVisible, setSearchBoxVisible] = useState(false);
	const searchBoxRef = useRef<HTMLDivElement | null>(null);

	const [isNonLeasedUdfUpdateModalOpen, setIsNonLeasedUdfUpdateModalOpen] = useState(false);
	const [isLeasedUdfUpdateModalOpen, setIsLeasedUdfUpdateModalOpen] = useState(false);

	const [openConfirmDeleteNLAModal, setOpenConfirmDeleteNLAModal] = useState(false);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
				setSearchBoxVisible(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const account = useSelector(selectAccount);

	// User roles for role-based actions
	const { data: user } = useGetCurrentUserQuery();
	const { role } = useUserAccountRole();
	const hasGeneralRequestAccess = useUserAccountRoleHasRoleScope(
		'CIRCADIA_INVOICES_GENERAL_REQUEST_ACCESS'
	);

	const buildInvoiceAssetsCsv = async (): Promise<Blob> => {
		if (!selectedInvoiceNumber) {
			throw new Error('Invoice number is required to export invoice asset details.');
		}

		const [invoiceHeader, firstPage] = await Promise.all([
			getInvoiceHeader(selectedInvoiceNumber).unwrap(),
			getInvoiceAssetsPage({
				invoiceNumber: selectedInvoiceNumber,
				page: 0,
				pageSize: INVOICE_ASSET_EXPORT_PAGE_SIZE,
				filters: activeFilters,
			}).unwrap(),
		]);

		const allAssets: InvoiceAsset[] = [...(firstPage.content ?? [])];
		const totalElements = firstPage.totalElements ?? allAssets.length;
		const totalPages = Math.max(1, Math.ceil(totalElements / INVOICE_ASSET_EXPORT_PAGE_SIZE));
		for (let page = 1; page < totalPages; page += 1) {
			const nextPage = await getInvoiceAssetsPage({
				invoiceNumber: selectedInvoiceNumber,
				page,
				pageSize: INVOICE_ASSET_EXPORT_PAGE_SIZE,
				filters: activeFilters,
			}).unwrap();
			allAssets.push(...(nextPage.content ?? []));
		}

		const rows = allAssets.map((asset) =>
			flattenInvoiceAssetRow(asset, invoiceHeader, account?.accountName ?? '')
		);
		const csvContent = generateCSV(rows, INVOICE_ASSET_EXPORT_CSV_HEADERS);
		return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	};

	const getExportCSV = async () => {
		if (!dataGridType.exportType) {
			return new Blob();
		}
		if (dataGridType.exportType === 'Users') {
			const users = await exportUsers(user?.userId ?? 0).unwrap();
			// need to create the CSV ourselves
			const csvContent = generateCSV(users, USERS_CSV_HEADERS);
			return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		}
		if (dataGridType.exportType === INVOICE_ASSETS_EXPORT) {
			return await buildInvoiceAssetsCsv();
		}
		const params: FilterSearchRequestParams = {
			accountId: account?.accountId ?? 0,
			accountTypeCode: account?.accountTypeCode ?? '',
			sourceId: account?.sourceId ?? 0,
			filters: activeFilters,
			// A selection takes precedence over the active filters; each export
			// endpoint decides which row property it needs as its id.
			selectedRows: selectedRows.length > 0 ? selectedRows : undefined,
			//...(selectedRows.length > 0 ? { selectedRows } : { filters: activeFilters }),
		};
		const exportFn = {
			Contracts: exportContracts,
			Assets: exportAssets,
			Invoices: exportInvoices,
			NonLeasedAssets: exportNLA,
		}[dataGridType.exportType];
		return await exportFn(params).unwrap();
	};

	// Fetch export and download CSV programmatically
	const handleExportDataClick = async () => {
		try {
			const blob = await getExportCSV();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${title}.csv`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
			handleCloseAllMenu();
		} catch (error) {
			setIsError(true);
			console.error('Error downloading CSV:', error);
		}
	};

	const handleDesktopMoreClick = (event: React.MouseEvent<HTMLDivElement>) => {
		setAnchorDesktop(event.currentTarget);
	};

	const handleMobileMoreClick = (event: React.MouseEvent<HTMLDivElement>) => {
		setAnchorMobile(event.currentTarget);
	};

	const handleCloseAllMenu = () => {
		anchorDesktop != null ? setAnchorDesktop(null) : null;
		anchorMobile != null ? setAnchorMobile(null) : null;
	};

	const handlePrintClick = () => {
		if (apiRef.current) {
			apiRef.current.exportDataAsPrint({ fileName: title });
		}
		handleCloseAllMenu();
	};

	const handleDownloadTemplateClick = () => {
		const csvString = [
			[
				'Schedule No.',
				'Start Date',
				'End Date',
				'Term',
				'Billing Address 1',
				'Billing Address 2',
				'Billing Address 3',
				'Billing Address 4',
				'Billing City',
				'Billing Zip Code',
				'Billing State',
				'Billing County',
				'Billing Country',
				'Asset Number',
				'Item Description',
				'Purchase Price',
				'Purchase Order No.',
				'Supplier Name',
				'Supplier Invoice No.',
				'Supplier Invoice Date',
				'Asset Category',
				'Serial Number',
				'Manufacturer',
				'Model Number',
				'Eq. Location Address 1',
				'Eq. Location Address 2',
				'Eq. Location Address 3',
				'Eq. Location Address 4',
				'Eq. Location City',
				'Eq. Location Zip Code',
				'Eq. Location State',
				'Eq. Location County',
			],
		]
			.map((row) => row.join(','))
			.join('\n');

		// Create a Blob from the CSV string
		const blob = new Blob([csvString], { type: 'text/csv' });

		// Generate a download link and initiate the download
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = 'Non-Leased Asset Import Template.csv';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
		handleCloseAllMenu();
	};

	const openDeleteConfirmationModal = () => {
		setOpenConfirmDeleteNLAModal(true);
		handleCloseAllMenu();
	};

	const openEditNonLeasedUDFModal = () => {
		setIsNonLeasedUdfUpdateModalOpen(true);
		handleCloseAllMenu();
	};

	const openEditLeasedUDFModal = () => {
		setIsLeasedUdfUpdateModalOpen(true);
		handleCloseAllMenu();
	};

	// Handler to open the upload modal
	const openUploadModal = () => {
		setIsUploadModalOpen(true);
		handleCloseAllMenu();
	};

	const openUploadLeasedAssetModal = () => {
		setIsUploadLeasedAssetModalOpen(true);
		handleCloseAllMenu();
	};

	// Handler to close the upload modal
	const closeUploadModal = () => {
		setIsUploadModalOpen(false);
	};

	const closeUploadLeasedAssetModal = () => {
		setIsUploadLeasedAssetModalOpen(false);
	};

	const closeNonLeasedUdfModal = () => {
		setIsNonLeasedUdfUpdateModalOpen(false);
	};

	const closeLeasedUdfModal = () => {
		setIsLeasedUdfUpdateModalOpen(false);
	};

	const closeDeleteNlaModal = () => {
		setOpenConfirmDeleteNLAModal(false);
	};

	let actionContext: DataGridActionContext = {
		handleRequestClick,
		handleExportDataClick,
		handlePrintClick,
		handleDownloadTemplateClick,
		role,
		dataGridType,
		openUploadModal,
		openEditNonLeasedUDFModal,
		openEditLeasedUDFModal,
		selectedRows,
		openUploadLeasedAssetModal,
		openDeleteConfirmationModal,
	};

	const moreActions = (dataGridType?.moreActions || [])
		.map((key) => ({ ...DataGridMoreActions[key] }))
		.filter(
			(action): action is DataGridMoreAction =>
				!!action && typeof action.label === 'string' && (!action.show || action.show(actionContext))
		);

	const MobileToolbarOptions = (
		<>
			<Backdrop
				sx={(theme) => ({
					color: theme.palette.brand.primary,
					zIndex: theme.zIndex.drawer + 1,
				})}
				open={globalIsLoading}
			>
				<CircularProgress color="inherit" size="5rem" />
			</Backdrop>
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				open={isError}
				autoHideDuration={2000}
				onClose={() => setIsError(false)}
			>
				<Alert
					onClose={() => setIsError(false)}
					severity="error"
					variant="filled"
					sx={{ width: '100%' }}
				>
					Error downloading CSV
				</Alert>
			</Snackbar>
			<Box
				sx={{ display: { xs: 'flex', md: 'none' }, cursor: 'pointer' }}
				onClick={handleMobileMoreClick}
			>
				<AddOutlinedIcon />
				<Typography
					variant="caption"
					sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
				>
					MORE
				</Typography>
			</Box>
			<Menu
				anchorEl={anchorMobile}
				open={Boolean(anchorMobile)}
				onClose={() => setAnchorMobile(null)}
			>
				{dataGridType.searchBox && (
					<MenuItem>
						<ListItemIcon>
							<SearchOutlined fontSize="small" />
						</ListItemIcon>
						Search
					</MenuItem>
				)}
				{dataGridType.exportType && (
					<MenuItem data-testid="mobile-export-csv-button" onClick={handleExportDataClick}>
						<ListItemIcon>
							<FileDownloadOutlinedIcon fontSize="small" />
						</ListItemIcon>
						Export as CSV
					</MenuItem>
				)}
				<MenuItem data-testid="mobile-print-button" onClick={handlePrintClick}>
					<ListItemIcon>
						<PrintOutlinedIcon fontSize="small" />
					</ListItemIcon>
					Print
				</MenuItem>
				{hasGeneralRequestAccess && (
					<MenuItem data-testid="mobile-request-button" onClick={handleRequestClick}>
						<ListItemIcon>
							<MailIcon fontSize="small" />
						</ListItemIcon>
						Request
					</MenuItem>
				)}
				{moreActions.map((action) => (
					<MenuItem
						key={action.key}
						data-testid={action.dataTestId}
						onClick={() => action.onClick?.(actionContext)}
					>
						<ListItemIcon>{action.icon}</ListItemIcon>
						{action.label}
					</MenuItem>
				))}
			</Menu>
		</>
	);

	const DesktopToolbarOptions = (
		<>
			<Backdrop
				sx={(theme) => ({
					color: theme.palette.brand.primary,
					zIndex: theme.zIndex.drawer + 1,
				})}
				open={globalIsLoading}
			>
				<CircularProgress color="inherit" size="5rem" />
			</Backdrop>
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				open={isError}
				autoHideDuration={2000}
				onClose={() => setIsError(false)}
			>
				<Alert
					onClose={() => setIsError(false)}
					severity="error"
					variant="filled"
					sx={{ width: '100%' }}
				>
					Error downloading CSV
				</Alert>
			</Snackbar>
			<Box
				sx={{
					display: { xs: 'none', md: 'flex' },
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 3,
				}}
			>
				{dataGridType.searchBox && (
					<Box
						ref={searchBoxRef}
						data-testid="search-button"
						sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
						onClick={() => setSearchBoxVisible(true)}
					>
						{!searchBoxVisible ? (
							<>
								<SearchOutlined fontSize="small" />
								<Typography variant="body1" component="span">
									Search
								</Typography>
							</>
						) : (
							React.createElement(dataGridType.searchBox)
						)}
					</Box>
				)}
				{dataGridType.printable && (
					<Box
						data-testid="export-print-button"
						sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
						onClick={handlePrintClick}
					>
						<PrintOutlinedIcon fontSize="small" />
						<Typography variant="body1" component="span">
							Print
						</Typography>
					</Box>
				)}
				{dataGridType.exportType && (
					<Box
						data-testid="export-csv-button"
						sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
						onClick={handleExportDataClick}
					>
						<FileDownloadOutlinedIcon fontSize="small" />
						<Typography variant="body1" component="span">
							Export
						</Typography>
					</Box>
				)}
				{dataGridType.requestTypes && hasGeneralRequestAccess && (
					<Box
						data-testid="request-button"
						sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
						onClick={handleRequestClick}
					>
						<MailIcon fontSize="small" />
						<Typography variant="body1" component="span">
							Request
						</Typography>
					</Box>
				)}
				{moreActions.length > 0 ? (
					<>
						<Box
							data-testid="more-button"
							sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
							onClick={handleDesktopMoreClick}
						>
							<MoreVertIcon fontSize="small" />
						</Box>
						<Menu
							anchorEl={anchorDesktop}
							open={Boolean(anchorDesktop)}
							onClose={() => setAnchorDesktop(null)}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'right',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
						>
							{moreActions.map((action) => (
								<MenuItem
									key={action.key}
									data-testid={action.dataTestId}
									onClick={() => action.onClick?.(actionContext)}
								>
									<ListItemIcon>{action.icon}</ListItemIcon>
									{action.label}
								</MenuItem>
							))}
						</Menu>
					</>
				) : null}
			</Box>
		</>
	);

	return (
		<>
			{MobileToolbarOptions}
			{DesktopToolbarOptions}
			<UploadNonLeasedAssetsModal
				open={isUploadModalOpen}
				onClose={closeUploadModal}
				onUploadSuccess={onRefresh}
			/>
			<UploadLeasedAssetUDFModal
				open={isUploadLeasedAssetModalOpen}
				onClose={closeUploadLeasedAssetModal}
			/>
			<ModifyLeasedUserDefinedFieldsModal
				open={isLeasedUdfUpdateModalOpen}
				onClose={closeLeasedUdfModal}
				selectedRows={selectedRows}
			/>
			<ModifyNonLeasedUserDefinedFieldsModal
				open={isNonLeasedUdfUpdateModalOpen}
				onClose={closeNonLeasedUdfModal}
				selectedRows={selectedRows}
			/>
			<DeleteNonLeasedAssetsModal
				open={openConfirmDeleteNLAModal}
				onClose={closeDeleteNlaModal}
				selectedRows={selectedRows}
				apiRef={apiRef}
			/>
		</>
	);
};

export default DataGridToolbarActionsComponent;
